import { FEATURES, SEARCH_GROUPS, MODAL, SEARCH_SIGNAL } from '@constants/Text'
import { lazyLoadView } from '@utils/LazyLoad'
import { isWelcomeDismissed } from '@modules/design/utils/welcome-storage'
import { comunicacoesFirstVisible } from '@modules/crm/utils/comunicacoes-channels'

// Quem tem a feature e já viu o welcome (marcado automaticamente na 1ª visita)
// vai direto para a overview; os demais (inclusive público de cross-sell) caem
// no welcome, que segue acessível pela aba "Bem-vindo". `require` lazy (não
// import no topo) — mesmo padrão de solicitacoes/routes.js
// (redirectSolicitacoesRoot): evita capturar o store errado em teste quando o
// jest.mock('@state/store') não hoista de forma confiável através de um
// módulo importado transitivamente.
const resolveCrmRedirect = () => {
  const store = require('@state/store').default
  const show = store.getters['parameters/show'] || {}
  if (show[FEATURES.CRM] && isWelcomeDismissed('crm')) {
    return { name: 'crm.overview' }
  }
  return { name: 'crm.welcome' }
}

// Entra no primeiro canal de comunicação visível (Atendimento > Email >
// Transcrições > Ligações). Email não tem feature flag, então sempre resolve
// — nunca cai em rota inexistente.
const resolveComunicacoesRedirect = () => {
  const store = require('@state/store').default
  const show = store.getters['parameters/show'] || {}
  const can = (feature) => !!show[feature]
  return { name: comunicacoesFirstVisible(can) || 'crm.emails' }
}

export default [
  {
    path: '/crm',
    name: 'crm',
    // customPermission: libera acesso se o escritório tem FEATURES.CRM OU se não tem OCULTAR_CROSS_SELL
    // (mantém a página de boas-vindas acessível como upsell para escritórios sem a contratação).
    meta: {
      customPermission: ({ show }) =>
        !!show[FEATURES.CRM] || !show[FEATURES.OCULTAR_CROSS_SELL],
      search: {
        title: 'CRM',
        description: 'Funil de leads, relacionamento e atendimento a clientes',
        keywords: [
          // Reforça a frase mesmo com `crm.leads` ('Funil de leads') também
          // indexada (Fase 4-8) — o title já cobre o match exato.
          'funil de leads',
          'leads',
          'prospecção',
          // O matcher é includes() sem stemming: plural cobre o singular
          // quando só acrescenta letras ('atendimentos' ⊃ 'atendimento').
          'atendimentos',
          'relacionamento',
          // Já 'mensagens' NÃO cobre 'mensagem' (o plural muda o radical) —
          // por isso as duas formas.
          'mensagem',
          'mensagens',
          'whatsapp',
          'email',
          'e-mail',
        ],
        icon: 'crm',
        group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
      },
    },
    redirect: resolveCrmRedirect,
    component: () =>
      lazyLoadView(import('@modules/crm/views/nav-bar/crm-nav-bar.vue')),
    children: [
      {
        path: 'bem-vindo',
        name: 'crm.welcome',
        // customPermission: welcome é acessível como upsell quando OCULTAR_CROSS_SELL está ausente;
        // retorna 404 apenas quando OCULTAR_CROSS_SELL=true e o escritório não tem FEATURES.CRM.
        meta: {
          customPermission: ({ show }) =>
            !!show[FEATURES.CRM] || !show[FEATURES.OCULTAR_CROSS_SELL],
        },
        component: () =>
          lazyLoadView(
            import('@modules/crm/views/welcome/view-crm-welcome.vue')
          ),
      },
      {
        path: 'overview',
        name: 'crm.overview',
        meta: {
          can_show: FEATURES.CRM,
          search: {
            title: 'CRM — Visão Geral',
            description: 'Panorama de relacionamento, leads e atividades',
            keywords: ['visão geral', 'panorama', 'resumo', 'dashboard'],
            group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
          },
        },
        component: () =>
          lazyLoadView(
            import('@modules/crm/views/overview/view-crm-overview.vue')
          ),
      },
      {
        path: 'relacionamento',
        name: 'crm.relationship',
        meta: {
          can_show: FEATURES.CRM,
          search: {
            title: 'Relacionamento',
            description: 'Prioridades, rotina mensal e histórico de clientes',
            keywords: [
              'relacionamento',
              'prioridades',
              'rotina',
              'clientes',
              'histórico',
            ],
            // Mesmo padrão de Clientes/Consolidador/Profissionais
            // (`router/routes.js`/`consolidator/routes.js`/
            // `professionals/routes.js`): ícone na página que hospeda
            // `actions`, herdado por elas via `action.icon || search.icon`
            // (`search-index.js:185`). Sem isso as ações do CRM caíam no
            // raio genérico por acidente (nenhuma página do CRM abaixo da
            // raiz declarava ícone), não por convenção real do app.
            icon: 'crm',
            group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
            actions: [
              {
                // Sem `to`: abre o modal em cima da tela atual, sem navegar
                // (ver CLAUDE.md deste módulo §3.1 "Ideia descartada").
                // global-modals.vue hospeda o modal real sempre montado,
                // independente de rota.
                id: 'adicionar-cliente-crm',
                title: 'Adicionar cliente',
                keywords: [
                  'novo cliente crm',
                  'cadastrar cliente relacionamento',
                  'criar cliente',
                  'adicionar contato',
                ],
                modal: MODAL.CRM_ADD_CLIENT,
              },
              {
                // D-1: sem meta.permission de propósito — crmBatchActions.js
                // só gateia "Exportar" por gestor; "Adicionar em lote" é
                // visível a qualquer perfil. Payload espelha
                // view-crm-relationship.vue:72. Sem `to` — ver comentário
                // acima.
                id: 'importar-clientes-lote',
                title: 'Adicionar clientes em lote',
                keywords: [
                  'adicionar em lote',
                  'cadastrar clientes em lote',
                  'criar clientes em lote',
                  'importar clientes',
                  'lote de clientes',
                  'csv',
                  'planilha',
                ],
                modal: MODAL.CRM_IMPORT_LEADS_BATCH,
                modalPayload: { mode: 'cliente' },
              },
            ],
          },
        },
        component: () =>
          lazyLoadView(
            import('@modules/crm/views/relationship/view-crm-relationship.vue')
          ),
        redirect: { name: 'crm.relationship.priorities' },
        children: [
          {
            path: 'prioridades',
            name: 'crm.relationship.priorities',
            component: () =>
              lazyLoadView(
                import(
                  '@modules/crm/views/relationship/view-crm-relationship-priorities.vue'
                )
              ),
            meta: {
              can_show: FEATURES.CRM,
              search: {
                title: 'Relacionamento — Prioridades',
                description: 'Atividades prioritárias de relacionamento',
                keywords: ['prioridades', 'relacionamento', 'atividades'],
                group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
              },
            },
          },
          {
            path: 'rotina-mensal',
            name: 'crm.relationship.monthlyRoutine',
            component: () =>
              lazyLoadView(
                import(
                  '@modules/crm/views/relationship/view-crm-relationship-monthly-routine.vue'
                )
              ),
            meta: {
              can_show: FEATURES.CRM,
              search: {
                title: 'Relacionamento — Rotina Mensal',
                description: 'Rotina mensal de contato com clientes',
                keywords: ['rotina mensal', 'relacionamento', 'contato'],
                group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
              },
            },
          },
          {
            path: 'clientes',
            name: 'crm.relationship.clients',
            component: () =>
              lazyLoadView(
                import(
                  '@modules/crm/views/relationship/view-crm-relationship-clients-overview.vue'
                )
              ),
            meta: {
              can_show: FEATURES.CRM,
              search: {
                title: 'Relacionamento — Clientes',
                description: 'Clientes acompanhados pela régua de relacionamento',
                keywords: ['clientes', 'relacionamento', 'régua'],
                group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
              },
            },
          },
          {
            path: 'historico',
            name: 'crm.relationship.historic',
            component: () =>
              lazyLoadView(
                import(
                  '@modules/crm/views/relationship/view-crm-relationship-historic.vue'
                )
              ),
            meta: {
              can_show: FEATURES.CRM,
              search: {
                title: 'Relacionamento — Tarefas já realizadas',
                description: 'Histórico de tarefas de relacionamento concluídas',
                keywords: [
                  'tarefas já realizadas',
                  'histórico',
                  'historico',
                  'relacionamento',
                ],
                group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
              },
            },
          },
        ],
      },
      {
        path: 'leads',
        name: 'crm.leads',
        meta: {
          can_show: FEATURES.CRM,
          search: {
            title: 'Funil de leads',
            description: 'Funil de leads em kanban e lista para prospecção',
            keywords: ['leads', 'funil', 'kanban', 'prospecção', 'pipeline'],
            // Ver comentário em crm.relationship (mesmo padrão).
            icon: 'crm',
            group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
            actions: [
              {
                // Sem `to` — ver comentário em crm.relationship (mesmo
                // padrão: abre o modal em cima da tela atual).
                id: 'cadastrar-lead',
                title: 'Cadastrar lead',
                keywords: [
                  'novo lead',
                  'criar lead',
                  'adicionar lead',
                  'prospect',
                  'cliente potencial',
                ],
                modal: MODAL.CRM_NEW_LEAD,
                modalPayload: { item: { new: true } },
              },
              {
                // D-1: sem meta.permission de propósito — crmBatchActions.js
                // só gateia "Exportar" por gestor; "Adicionar em lote" é
                // visível a qualquer perfil. Payload interno espelha
                // view-crm-leads-tabs.vue:71. Sem `to` — ver comentário
                // acima.
                id: 'importar-leads-lote',
                title: 'Adicionar leads em lote',
                keywords: [
                  'adicionar em lote',
                  'cadastrar leads em lote',
                  'criar leads em lote',
                  'importar leads',
                  'lote de leads',
                  'csv',
                  'planilha',
                ],
                modal: MODAL.CRM_IMPORT_LEADS_BATCH,
                modalPayload: { mode: 'lead' },
              },
            ],
          },
        },
        component: () =>
          lazyLoadView(
            import('@modules/crm/views/leads/view-crm-leads-tabs.vue')
          ),
        redirect: { name: 'crm.leads.overview' },
        children: [
          {
            path: 'visao_geral',
            name: 'crm.leads.overview',
            component: () =>
              lazyLoadView(
                import(
                  '@modules/crm/views/leads/overview/view-overview-leads.vue'
                )
              ),
            meta: {
              can_show: FEATURES.CRM,
              search: {
                title: 'Leads — Visão geral',
                description: 'Panorama do funil de leads',
                keywords: ['leads', 'visão geral', 'panorama', 'funil'],
                group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
              },
            },
          },
          {
            path: 'kanban',
            name: 'crm.leads.kanban',
            component: () =>
              lazyLoadView(
                import(
                  '@modules/crm/views/leads/leads-kanban/view-crm-leads.vue'
                )
              ),
            meta: {
              can_show: FEATURES.CRM,
              isCompliance: true,
              search: {
                title: 'Leads — Pipeline',
                description: 'Funil de leads em formato kanban',
                keywords: ['pipeline', 'kanban', 'funil', 'leads'],
                group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
              },
            },
          },
          {
            path: 'lista',
            name: 'crm.leads.list',
            component: () =>
              lazyLoadView(
                import(
                  '@modules/crm/views/leads/leads-list/view-crm-leads-list.vue'
                )
              ),
            meta: {
              can_show: FEATURES.CRM,
              search: {
                title: 'Leads — Lista',
                description: 'Lista de leads em formato de tabela',
                keywords: ['leads', 'lista', 'tabela'],
                group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
              },
            },
          },
        ],
      },
      {
        path: 'calendario',
        name: 'crm.calendar',
        meta: {
          can_show: FEATURES.CRM,
          search: {
            title: 'Calendário do CRM',
            description: 'Agenda de compromissos e atividades do CRM',
            keywords: ['calendário', 'agenda', 'compromissos', 'atividades'],
            // Ver comentário em crm.relationship (mesmo padrão).
            icon: 'crm',
            group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
            actions: [
              {
                // QA ao vivo confirmou (2026-09): abrir sem cliente/lead com
                // este payload literal funciona standalone fora do CRM (testado
                // a partir de /comissoes) — modal abre com defaults vazios e o
                // save chega no backend (200). Título espelha o texto real do
                // botão de fallback do próprio fluxo
                // (crm-timeline-table.vue:909, showModal "Agendar tarefa"),
                // não o `modalTitle` do modal em si (que é só "Agendar").
                // Sem meta.permission de propósito: os 4 disparadores reais
                // deste modal (view-crm-calendar.vue:604/849/861,
                // crm-timeline-table.vue:888) gateiam por autenticação de
                // provedor externo (Google/Microsoft), nunca por gestor.
                id: 'agendar-tarefa',
                title: 'Agendar tarefa',
                keywords: [
                  'agendar',
                  'novo agendamento',
                  'agendar evento',
                  'criar evento',
                  'criar tarefa',
                  'cadastrar tarefa',
                  'adicionar tarefa',
                  'nova tarefa',
                  'marcar reunião',
                ],
                to: { name: 'crm.calendar' },
                modal: MODAL.CRM_SCHEDULE_TASK_CALENDAR,
                modalPayload: {
                  item: {},
                  saveHistoric: true,
                  providersAvailable: [],
                },
              },
            ],
          },
        },
        component: () =>
          lazyLoadView(
            import('@modules/crm/views/calendar/view-crm-calendar.vue')
          ),
      },
      {
        // Shell "Omnichannel" (rótulo visível — route name interno ficou como
        // crm.comunicacoes) — BaseSegmentedTabs + <router-view>: agrupa
        // Atendimento/Email/Transcrições/Ligações, cada um como rota filha
        // navegável por URL. Os 4 `name` abaixo são os mesmos de antes do
        // agrupamento (só o `path` mudou) — preserva todo consumidor
        // name-based (popup do WhatsApp, blacklist, back-links, nav-bar).
        path: 'comunicacoes',
        name: 'crm.comunicacoes',
        meta: {
          // Email não tem feature própria e sempre aparece pra quem tem CRM.
          can_show: FEATURES.CRM,
          search: {
            // Rótulo visível é "Omnichannel" (constants/Text.js — crmRoutes()),
            // não "Comunicações" — o route name interno não mudou.
            title: 'Omnichannel',
            description:
              'Mensagens, e-mails, transcrições e ligações em um só lugar',
            keywords: [
              'comunicações',
              'comunicacoes',
              'omnichannel',
              'canais',
              'atendimento',
              'e-mail',
              'transcrições',
              'ligações',
            ],
            group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/crm/views/comunicacoes/view-crm-comunicacoes.vue'
            )
          ),
        redirect: resolveComunicacoesRedirect,
        children: [
          {
            // Atendimento WhatsApp incorporado como aba do CRM. Reutiliza
            // diretamente o view-atendimento.vue do módulo de atendimento.
            path: 'atendimento',
            name: 'crm.atendimento',
            meta: {
              can_show: FEATURES.ATENDIMENTO,
              search: {
                title: 'Mensagens',
                description: 'Atendimento via WhatsApp integrado ao CRM',
                keywords: ['atendimento', 'whatsapp', 'chat', 'mensagens'],
                group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
              },
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/atendimento/views/view-atendimento/view-atendimento.vue'
                )
              ),
          },
          {
            path: 'emails',
            name: 'crm.emails',
            component: () =>
              lazyLoadView(
                import(
                  '@modules/crm/views/view-crm-emails/view-crm-emails.vue'
                )
              ),
            meta: {
              can_show: FEATURES.CRM,
              search: {
                title: 'E-mails',
                description: 'Envio e histórico de e-mails com clientes',
                keywords: ['e-mail', 'email', 'mensagens', 'comunicação'],
                group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
              },
            },
          },
          {
            path: 'transcricoes',
            name: 'crm.transcricoes',
            meta: {
              can_show: FEATURES.TRANSCRICOES,
              search: {
                title: 'Transcrições',
                description:
                  'Transcrições de reuniões e análise de sentimento',
                keywords: [
                  'transcrições',
                  'reuniões',
                  'áudio',
                  'sentimento',
                  'gravações',
                ],
                // Ver comentário em crm.relationship (mesmo padrão).
                icon: 'crm',
                group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
                actions: [
                  {
                    // Gestor-only (D-2 confirmado). Sem `to` — ver comentário
                    // em crm.relationship (mesmo padrão). `open({ project } =
                    // {})` tolera emissão sem payload próprio — espelha
                    // view-crm-transcricoes-projects.vue:34.
                    id: 'novo-projeto-transcricao',
                    title: 'Novo projeto de transcrição',
                    keywords: [
                      'novo projeto',
                      'criar projeto',
                      'cadastrar projeto',
                      'adicionar projeto',
                      'projeto de reunião',
                      'critérios',
                    ],
                    modal: MODAL.TRANSCRIPT_PROJECT_EDIT,
                    meta: { permission: ['gestor'] },
                  },
                  {
                    // 2 canais de propósito (chegada de outra tela via query
                    // + clique repetido via GlobalBus) — ver crm/CLAUDE.md
                    // §3.1 "Minhas transcrições" para o racional completo.
                    id: 'minhas-transcricoes',
                    title: 'Minhas transcrições',
                    keywords: [
                      'somente eu',
                      'modo eu',
                      'minhas reuniões',
                      'meu histórico',
                    ],
                    to: {
                      name: 'crm.transcricoes.history',
                      query: { modo_eu: '1' },
                    },
                    modal: SEARCH_SIGNAL.CRM_TRANSCRICOES_MODO_EU,
                    meta: { permission: ['gestor'] },
                  },
                ],
              },
            },
            // Shell aninhado (BaseSegmentedTabs leve + <router-view>): cada
            // aba é uma rota filha navegável por URL. O detalhe abre via
            // modal (GlobalBus MODAL.TRANSCRIPT_DETAIL), não é rota.
            component: () =>
              lazyLoadView(
                import(
                  '@modules/crm/views/transcricoes/view-crm-transcricoes.vue'
                )
              ),
            redirect: { name: 'crm.transcricoes.live' },
            children: [
              {
                path: 'ao-vivo',
                name: 'crm.transcricoes.live',
                meta: {
                  can_show: FEATURES.TRANSCRICOES,
                  search: {
                    title: 'Transcrições — Ao vivo',
                    description: 'Transcrição de reuniões em andamento',
                    keywords: ['ao vivo', 'transcrições', 'reunião'],
                    group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
                  },
                },
                component: () =>
                  lazyLoadView(
                    import(
                      '@modules/crm/views/transcricoes/views/view-crm-transcricoes-live.vue'
                    )
                  ),
              },
              {
                path: 'historico',
                name: 'crm.transcricoes.history',
                meta: {
                  can_show: FEATURES.TRANSCRICOES,
                  search: {
                    title: 'Transcrições — Histórico',
                    description: 'Histórico de transcrições de reuniões',
                    keywords: ['histórico', 'historico', 'transcrições'],
                    group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
                  },
                },
                component: () =>
                  lazyLoadView(
                    import(
                      '@modules/crm/views/transcricoes/views/view-crm-transcricoes-history.vue'
                    )
                  ),
              },
              {
                // Gestor-only (defensivo — segmented oculta a aba + empty-state).
                path: 'dashboard',
                name: 'crm.transcricoes.dashboard',
                meta: {
                  can_show: FEATURES.TRANSCRICOES,
                  search: {
                    title: 'Transcrições — Dashboard',
                    description: 'Indicadores das transcrições de reuniões',
                    keywords: ['dashboard', 'transcrições', 'indicadores'],
                    group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
                    // Gate de DESCOBERTA (search-index.js) — reflete o mesmo
                    // gestorOnly já aplicado no segmented (transcricoes-sub-nav.js).
                    meta: { permission: ['gestor'] },
                  },
                },
                component: () =>
                  lazyLoadView(
                    import(
                      '@modules/crm/views/transcricoes/views/view-crm-transcricoes-dashboard.vue'
                    )
                  ),
              },
              {
                // Gestor-only (defensivo — segmented oculta a aba + empty-state).
                path: 'projetos',
                name: 'crm.transcricoes.projects',
                meta: {
                  can_show: FEATURES.TRANSCRICOES,
                  search: {
                    title: 'Transcrições — Projetos',
                    description: 'Projetos e critérios de avaliação de transcrições',
                    keywords: ['projetos', 'transcrições', 'critérios'],
                    group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
                    // Gate de DESCOBERTA (search-index.js) — reflete o mesmo
                    // gestorOnly já aplicado no segmented (transcricoes-sub-nav.js).
                    meta: { permission: ['gestor'] },
                  },
                },
                component: () =>
                  lazyLoadView(
                    import(
                      '@modules/crm/views/transcricoes/views/view-crm-transcricoes-projects.vue'
                    )
                  ),
              },
            ],
          },
          {
            path: 'ligacoes',
            name: 'crm.ligacoes',
            meta: {
              // Array = AND no guard do router: exige CRM e a flag opt-in de Ligações
              can_show: [FEATURES.CRM, FEATURES.CRM_LIGACOES],
              search: {
                title: 'Ligações',
                description: 'Registro e histórico de ligações com clientes',
                keywords: ['ligações', 'chamadas', 'telefone', 'calls'],
                group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
              },
            },
            component: () =>
              lazyLoadView(
                import('@modules/crm/views/ligacoes/view-crm-ligacoes.vue')
              ),
          },
        ],
      },
      {
        // Proposta R2 — monta o payload do deck comercial 16:9 a partir do que a
        // plataforma já sabe, e deixa o consultor escrever o que só ele sabe.
        //
        // `idClienteApp` é quem dá endereço à geração: as três rotas do backend
        // vivem em `/clients/{id_cliente_app}/proposta-r2/...`, e é esse
        // segmento que habilita o `decorator_check_client_app_access` — a única
        // barreira de tenant do fluxo. Não dá para derivar do `analiseId`, que é
        // opcional: escritório sem Fee Lab não tem análise nenhuma.
        //
        // ★ Opcional, e não obrigatório, por causa da aba: a nav-bar navega com
        // `push({ name, params })` e a entrada de `constants/Text.js` não leva
        // params (`nav-bar-navigation.vue:176`). Medido — com o segmento
        // obrigatório, o `resolve` levanta *missing param for named route* e o
        // clique na aba quebra em vez de abrir a tela. Sem cliente a tela abre,
        // mostra o que tem e explica por que não dá para gerar; o que ela nunca
        // faz é disparar a geração e colher um 400 mudo.
        //
        // `analiseId` também opcional: com ele a tela carrega o cenário salvo do
        // Fee Lab; sem ele, as folhas que dependem do cenário saem do deck pela
        // degradação declarada em `proposta-r2/contrato-r2.js`.
        //
        // Array = AND no guard (mesma semântica de crm.ligacoes): exige CRM e a
        // flag opt-in. Sem registro no banco, a rota responde 404 e a aba some.
        path: 'proposta-r2/:idClienteApp?/:analiseId?',
        name: 'crm.proposta-r2',
        props: true,
        meta: {
          can_show: [FEATURES.CRM, FEATURES.CRM_PROPOSTA_R2],
          search: {
            title: 'Proposta R2',
            description:
              'Monta a proposta comercial da segunda reunião com os dados da plataforma',
            keywords: ['proposta', 'r2', 'deck', 'apresentação', 'comercial'],
            group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
          },
        },
        component: () =>
          lazyLoadView(
            import('@modules/crm/views/proposta-r2/view-crm-proposta-r2.vue')
          ),
      },
      {
        path: 'oportunidades',
        name: 'crm.opportunities',
        meta: {
          can_show: FEATURES.CRM,
          search: {
            title: 'Oportunidades do CRM',
            description: 'Oportunidades comerciais e de negócio no CRM',
            keywords: ['oportunidades', 'negócios', 'vendas', 'comercial'],
            // Ver comentário em crm.relationship (mesmo padrão).
            icon: 'crm',
            group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
            actions: [
              {
                // Sem `to` — ver comentário em crm.relationship (mesmo
                // padrão).
                id: 'criar-oportunidade',
                title: 'Criar oportunidade',
                keywords: [
                  'nova oportunidade',
                  'cadastrar oportunidade',
                  'adicionar oportunidade',
                  '+ oportunidade',
                  'negócio',
                  'deal',
                  'venda',
                ],
                modal: MODAL.CRM_OPPORTUNITIES,
                modalPayload: {
                  id_funil: null,
                  dontReloadRegRelationshipModal: true,
                },
              },
            ],
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/crm/views/opportunities/view-crm-opportunities.vue'
            )
          ),
      },
      {
        path: 'grupos-familiares',
        name: 'crm.grupos-familiares',
        meta: {
          // Array = AND no guard do router: exige CRM e a flag opt-in de
          // Grupos Familiares (mesmo formato de crm.ligacoes).
          can_show: [FEATURES.CRM, FEATURES.FAMILY_GROUP],
          search: {
            title: 'Grupos Familiares',
            description:
              'Agrupe clientes da mesma família para acompanhar o patrimônio em conjunto.',
            keywords: [
              'grupo familiar',
              'grupos familiares',
              'família',
              'familia',
              'patrimônio conjunto',
            ],
            group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/crm/views/grupos-familiares/view-crm-grupos-familiares.vue'
            )
          ),
      },
    ],
  },
]
